import React from 'react';
import AbstractView from '../AbstractView';
import ErrorRepresentation from '../../ErrorRepresentation/ErrorRepresentation';
import {LayoutContext} from '../../../contexts/LayoutContext';
import ErrorBoundary from '../../ErrorBoundary';
import {IFailedHttpRequest} from '../../../@interfaces/IFailedHttpRequest';
import {ConfigurableViewProps} from '../../../@types/components/ConfigurableView';
import {RequireOne} from '../../../@types/RequireOne';

export default abstract class ConfigurableView<
        P extends ConfigurableViewProps = ConfigurableViewProps,
        TConfig = any
> extends AbstractView<P> {
    protected static pageTitle = 'ConfigurableView.page-title.loading';
    protected static pageTitleError = 'ConfigurableView.page-title.error';

    static contextType = LayoutContext;
    context!: React.ContextType<typeof LayoutContext>;

    constructor(props: any) {
        super(props);

        this.retryLoadConfig = this.retryLoadConfig.bind(this);
        this.renderAccessDeniedError = this.renderAccessDeniedError.bind(this);
    }

    componentDidMount() {
        this.prepareConfig();

        this.updatePageTitleAndBreadcrumbs();
    }

    componentDidUpdate(prevProps: Readonly<P>) {
        const controllerNameChanged = prevProps.controllerName !== this.props.controllerName;
        const configChanged = controllerNameChanged ? null : (
                JSON.stringify(this.getConfig(prevProps)) !== JSON.stringify(this.getConfig())
        );

        if (controllerNameChanged) {
            this.prepareConfig();
        }

        if (controllerNameChanged || configChanged) {
            this.updatePageTitleAndBreadcrumbs();
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        if (!this.getConfig() && (this.getConfigError() || this.nowLoading())) {
            this.cancelConfigLoading();
        }
    }

    render() {
        return <ErrorBoundary>
            {
                this.getConfigError() ?
                        this.renderErrorRepresentation() :
                        super.render()
            }
        </ErrorBoundary>;
    }

    private prepareConfig() {
        if (!this.getConfig() && !this.nowLoading()) {
            this.loadConfig();
        }
    }

    protected abstract getConfig(props?: Readonly<P>): TConfig | null;

    protected abstract getConfigError(props?: Readonly<P>): IFailedHttpRequest | null;

    protected abstract loadConfig(): void;

    protected abstract cancelConfigLoading(props?: Readonly<P>): void;

    protected abstract retryLoadConfig(): void;

    protected abstract nowLoading(props?: Readonly<P>): boolean;

    protected abstract renderContent(): React.ReactNode;

    protected renderAccessDeniedError() {
        return <ErrorRepresentation error={{
            name: 'Exceptions.services.http.access_denied',
            message: 'Exceptions.services.http.access_denied'
        }} outsideContainer={true}/>;
    }

    protected renderErrorRepresentation() {
        const error = this.getConfigError();
        if (!error) {
            return null;
        }

        return <ErrorRepresentation
                error={error.error}
                outsideContainer={true}
                retryInProcess={this.nowLoading()}
                retryButtonLabel="ConfigurableView.retry-failed-request.retry-button-label"
                retryOnClick={this.retryLoadConfig}
                retryLastAttemptAt={error.lastAttemptAt}
                retryAttempts={error.attempts}
                nextTryInSecondsText="ConfigurableView.retry-failed-request.next-try-in-number-seconds"
                nextTrySoonText="ConfigurableView.retry-failed-request.next-try-soon"
                nextTryNowText="ConfigurableView.retry-failed-request.loading"
                triedAttemptsText="ConfigurableView.retry-failed-request.attempts-number"
        />;
    }

    protected renderMetaTags() {
        if (this.props.isIsolatedComponent) {
            return null;
        }

        return super.renderMetaTags();
    }

    protected getPageTitle(): string {
        if (this.getConfigError()) {
            return (this.constructor as typeof ConfigurableView)?.pageTitleError ?? '';
        }

        return this.getPageTitleFromConfig() ?? super.getPageTitle();
    }

    protected getPageMetadataToUpdate() {
        type Meta = Parameters<typeof this.context.setPageMeta>[0];
        const meta: RequireOne<Meta, 'breadcrumbsItems'> = {
            pageTitle: this.getPageTitleFromConfig() ?? undefined,
            pageSubTitle: this.getPageSubTitleFromConfig() ?? undefined,
            breadcrumbsItems: [],
        };

        const label = meta.pageTitle ?? meta.pageSubTitle;
        if (label) {
            meta.breadcrumbsItems.push({label, uri: '#'});
        }

        return meta;
    }

    protected updatePageTitleAndBreadcrumbs(): void {
        if (this.props.isIsolatedComponent || !this.context) {
            return;
        }

        this.context.setPageMeta(this.getPageMetadataToUpdate());
    }

    protected getPageTitleFromConfig(): string | null {
        const config = this.getConfig();
        if (config != null && typeof config === 'object' && 'title' in config) {
            return (config as any).title;
        }

        return null;
    }

    protected getPageSubTitleFromConfig(): string | null {
        return null;
    }
}
